import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AccountsSubNav from '../components/AccountsSubNav'
import SharedFilters from '../../../../components/SharedFilters'
import AppActionMenu from '../../../../components/AppActionMenu'
import AppPagination from '../../../../components/AppPagination'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { listAccountHeads, archiveAccountHead, deleteAccountHead, getAcGroups } from '../heads/api/acHeadApi'

export default function VendorListScreen() {
  const navigate = useNavigate()
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [departmentFilter, setDepartmentFilter] = useState('All')
  const [placeFilter, setPlaceFilter] = useState('All')
  const [groupFilter, setGroupFilter] = useState('All')
  const [filterFilter, setFilterFilter] = useState('All')
  const [vendorFilter, setVendorFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('Active')
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [vendorGroupId, setVendorGroupId] = useState(null)

  useEffect(() => {
    getAcGroups().then(groups => {
      const vGroup = groups.find(g => (g.Name || '').toLowerCase().includes('creditor') || (g.Name || '').toLowerCase().includes('vendor'))
      if (vGroup) setVendorGroupId(vGroup.ID)
    }).catch(() => {})
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = {
        searchText: searchText || (vendorFilter !== 'All' ? vendorFilter : undefined),
        archive: statusFilter === 'All' ? undefined : (statusFilter === 'Archive' ? 1 : 0),
        groupId: vendorGroupId || undefined,
        page,
        pageSize: 25,
      }
      const data = await listAccountHeads(params)
      const rows = (Array.isArray(data) ? data : data?.items || []).map(row => ({
        id: row.ID,
        name: row.Name,
        entries: row.CostCenter || 0,
        opBal: row.Budget || 0.0,
        amount: 0.0,
        adjusted: 0.0,
        due: 0.0,
        balance: row.Budget || 0.0,
        unadjust: 0.0,
        ledger: row.Budget || 0.0,
        status: row.Archive ? 'Archive' : 'Active',
      }))
      setItems(rows)
    } catch (e) {
      console.error('Failed to load vendors', e)
    } finally {
      setIsLoading(false)
    }
  }, [searchText, vendorFilter, statusFilter, vendorGroupId, page])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleReset = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setDepartmentFilter('All')
    setPlaceFilter('All')
    setGroupFilter('All')
    setFilterFilter('All')
    setVendorFilter('All')
    setStatusFilter('Active')
    setSearchField('Name')
    setSearchText('')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  const handleArchive = async (id) => {
    try {
      await archiveAccountHead(id)
      appToast.success('Vendor status updated.')
      loadData()
    } catch (e) {
      appToast.error('Failed to update vendor status.')
    }
  }

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Vendor',
      message: `Delete Vendor "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteAccountHead(id)
      appToast.success(`Vendor "${name}" deleted successfully.`)
      loadData()
    } catch (e) {
      appToast.error('Failed to delete vendor.')
    }
  }

  const filteredItems = items

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Vendors
          </h1>
        </div>
        <AccountsSubNav />
      </section>

      {/* Shared Filters Accordion */}
      <SharedFilters
        searchField={searchField}
        searchText={searchText}
        statusFilter={statusFilter}
        companyFilter={companyFilter}
        branchFilter={branchFilter}
        departmentFilter={departmentFilter}
        placeFilter={placeFilter}
        groupFilter={groupFilter}
        filterFilter={filterFilter}
        vendorFilter={vendorFilter}
        fromDate={fromDate}
        toDate={toDate}

        setSearchField={setSearchField}
        setSearchText={setSearchText}
        setStatusFilter={setStatusFilter}
        setCompanyFilter={setCompanyFilter}
        setBranchFilter={setBranchFilter}
        setDepartmentFilter={setDepartmentFilter}
        setPlaceFilter={setPlaceFilter}
        setGroupFilter={setGroupFilter}
        setFilterFilter={setFilterFilter}
        setVendorFilter={setVendorFilter}
        setFromDate={setFromDate}
        setToDate={setToDate}
        onReset={handleReset}

        searchFieldOptions={[{ value: 'Name', label: 'Name' }]}
        statusOptions={[{ value: 'Active', label: 'Active' }, { value: 'Archive', label: 'Archived' }, { value: 'All', label: 'All' }]}
        companyOptions={[{ value: 'All', label: 'All Companies' }]}
        branchOptions={[{ value: 'All', label: 'All Branches' }]}
        departmentOptions={[{ value: 'All', label: 'All Departments' }]}
        placeOptions={[{ value: 'All', label: 'All Places' }]}
        groupOptions={[{ value: 'All', label: 'All Groups' }]}
        filterOptions={[{ value: 'All', label: 'All Filters' }]}
        vendorOptions={[{ value: 'All', label: 'All Vendors' }]}
        visibleAccordionFilters={['company', 'branch', 'department', 'place', 'group', 'filter', 'vendor']}
        tools={[
          { key: 'send-sms', label: 'Send SMS', icon: 'sms' },
          { key: 'print', label: 'Print', icon: 'print' },
          { key: 'download', label: 'Download', icon: 'download' }
        ]}
      />

      {/* Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="px-5 py-3.5">Name</th>
                <th className="px-5 py-3.5 text-center">Entries</th>
                <th className="px-5 py-3.5 text-right">OpBal</th>
                <th className="px-5 py-3.5 text-right">Amount</th>
                <th className="px-5 py-3.5 text-right">Adjusted</th>
                <th className="px-5 py-3.5 text-right">Due</th>
                <th className="px-5 py-3.5 text-right">Balance</th>
                <th className="px-5 py-3.5 text-right">Unadjust</th>
                <th className="px-5 py-3.5 text-right">Ledger</th>
                <th className="w-20 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    No entries meet selected criteria.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-surface-container-low/40">
                    <td className="px-5 py-3.5 font-bold text-on-surface">{item.name}</td>
                    <td className="px-5 py-3.5 text-center font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">{item.entries}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.opBal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.adjusted.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.due.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.unadjust.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">₹{item.ledger.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3.5 text-right">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                        items={[
                          { key: 'edit', label: 'Edit', icon: 'edit' },
                          { key: 'archive', label: item.status === 'Active' ? 'Archive' : 'Unarchive', icon: item.status === 'Active' ? 'archive' : 'unarchive' },
                          { key: 'delete', label: 'Delete', icon: 'delete' },
                          { key: 'address', label: 'Additional Address', icon: 'home' },
                          { key: 'unadjusted', label: 'Unadjusted', icon: 'adjust' },
                          { key: 'multi-column-report', label: 'Multi-Column Report', icon: 'table_chart' },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') navigate(`/masters/acheads/vendors/entry?id=${item.id}`)
                          else if (key === 'archive') handleArchive(item.id)
                          else if (key === 'delete') handleDelete(item.id, item.name)
                          else if (key === 'address') appToast.info(`Opening Additional Address for vendor "${item.name}"`)
                          else if (key === 'unadjusted') appToast.info(`Opening Unadjusted ledger details for vendor "${item.name}"`)
                          else if (key === 'multi-column-report') appToast.info(`Generating Multi-Column Report for vendor "${item.name}"`)
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {filteredItems.length} entries
          </p>
          <AppPagination
            page={page}
            totalPages={1}
            onPageChange={setPage}
            canPrevious={false}
            canNext={false}
          />
        </div>
      </section>
    </div>
  )
}
